import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchRealEstateSources,
  syncRealEstateSources,
} from "../../../../services/realEstateSourceControls";
import { fetchWeatherSources } from "../../../../services/weatherSourceControls";
import { fetchAutomotivePipelineSources } from "../../../../services/automotiveSourceControls";
import { listCultureAdminSources } from "../../../../api/cultureSourceControls";
import { getAdminDataSources } from "../../../../services/midpointApi";
import { getVehicleMarketSources } from "../../../../api/vehicleMarketSourceApi";
import { listFinanceFxAdminSources } from "../../../../api/financeFxSourceControls";
import { listGa4AnalyticsAdminSources } from "../../../../api/ga4AnalyticsSourceControls";
import { listDataSourceSchedules } from "../../../../api/dataSourceScheduleApi";
import { fetchTransportSchedulerJobs } from "../../../../api/transportSchedulerApi";
import {
  applyScheduleOverlay,
  mergeTransportJobSchedule,
  overlayScheduleMap,
} from "../dataSources/dataSourceScheduleUtils";

/** Full catalog refresh while idle; running polls are slower and domain-scoped. */
const POLL_INTERVAL_MS = 10000;

export const DATA_SOURCE_POLL_DOMAINS = {
  realEstate: "realEstate",
  weather: "weather",
  automotive: "automotive",
  culture: "culture",
  finance: "finance",
  ga4: "ga4",
};

function isRealEstateSourceRunning(source) {
  return (
    source.isRunning ||
    source.health === "running" ||
    source.health === "queued" ||
    source.latestRun?.status === "RUNNING" ||
    source.latestRun?.status === "QUEUED"
  );
}

function isWeatherSourceRunning(source) {
  return source.isRunning || source.health === "running";
}

function isCultureSourceRunning(source) {
  return source.isRunning;
}

function isFinanceSourceRunning(source) {
  return source.isRunning || source.health === "running";
}

function isGa4SourceRunning(source) {
  return source.isRunning || source.health === "running";
}

function isAutomotiveSourceRunning(source) {
  return source.isRunning || source.health === "running" || source.lastStatus === "running";
}

export function resolveRunningPollDomains({
  realEstateSources = [],
  weatherSources = [],
  automotiveSources = [],
  cultureSources = [],
  financeSources = [],
  ga4Sources = [],
} = {}) {
  const domains = [];
  if (realEstateSources.some(isRealEstateSourceRunning)) {
    domains.push(DATA_SOURCE_POLL_DOMAINS.realEstate);
  }
  if (weatherSources.some(isWeatherSourceRunning)) {
    domains.push(DATA_SOURCE_POLL_DOMAINS.weather);
  }
  if (automotiveSources.some(isAutomotiveSourceRunning)) {
    domains.push(DATA_SOURCE_POLL_DOMAINS.automotive);
  }
  if (cultureSources.some(isCultureSourceRunning)) {
    domains.push(DATA_SOURCE_POLL_DOMAINS.culture);
  }
  if (financeSources.some(isFinanceSourceRunning)) {
    domains.push(DATA_SOURCE_POLL_DOMAINS.finance);
  }
  if (ga4Sources.some(isGa4SourceRunning)) {
    domains.push(DATA_SOURCE_POLL_DOMAINS.ga4);
  }
  return domains;
}

export function useDataSourceControls() {
  const [realEstateSources, setRealEstateSources] = useState([]);
  const [weatherSources, setWeatherSources] = useState([]);
  const [automotiveSources, setAutomotiveSources] = useState([]);
  const [cultureSources, setCultureSources] = useState([]);
  const [domainSources, setDomainSources] = useState({
    food: [],
    transport: [],
    culture: [],
    automotive: [],
    vehicle: [],
    vendor_directory: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [realEstateErrorMessage, setRealEstateErrorMessage] = useState("");
  const [weatherErrorMessage, setWeatherErrorMessage] = useState("");
  const [automotiveErrorMessage, setAutomotiveErrorMessage] = useState("");
  const [cultureErrorMessage, setCultureErrorMessage] = useState("");
  const [vehicleMarketSources, setVehicleMarketSources] = useState([]);
  const [vehicleMarketErrorMessage, setVehicleMarketErrorMessage] = useState("");
  const [financeSources, setFinanceSources] = useState([]);
  const [financeErrorMessage, setFinanceErrorMessage] = useState("");
  const [ga4Sources, setGa4Sources] = useState([]);
  const [ga4ErrorMessage, setGa4ErrorMessage] = useState("");
  const [toast, setToast] = useState(null);
  const pollRef = useRef(null);
  const runningDomainsRef = useRef([]);

  const showToast = useCallback((message, tone = "neutral") => {
    setToast({ message, tone, id: Date.now() });
  }, []);

  const loadSources = useCallback(async ({ silent = false, domains = null } = {}) => {
    const include = (domain) => !domains || domains.includes(domain);
    const fullLoad = !domains;

    try {
      if (!silent) {
        setIsLoading(true);
      }

      if (include(DATA_SOURCE_POLL_DOMAINS.realEstate)) {
        setRealEstateErrorMessage("");
      }
      if (include(DATA_SOURCE_POLL_DOMAINS.weather)) {
        setWeatherErrorMessage("");
      }
      if (include(DATA_SOURCE_POLL_DOMAINS.automotive)) {
        setAutomotiveErrorMessage("");
      }

      const requests = [];
      const requestKeys = [];

      if (include(DATA_SOURCE_POLL_DOMAINS.realEstate)) {
        requestKeys.push("realEstate");
        requests.push(fetchRealEstateSources());
      }
      if (include(DATA_SOURCE_POLL_DOMAINS.weather)) {
        requestKeys.push("weather");
        requests.push(fetchWeatherSources());
      }
      if (include(DATA_SOURCE_POLL_DOMAINS.automotive)) {
        requestKeys.push("automotive");
        requests.push(fetchAutomotivePipelineSources());
      }
      if (include(DATA_SOURCE_POLL_DOMAINS.culture) || fullLoad) {
        requestKeys.push("culture");
        requests.push(listCultureAdminSources());
      }
      if (fullLoad) {
        requestKeys.push("adminSources");
        requests.push(getAdminDataSources());
        requestKeys.push("vehicleMarket");
        requests.push(getVehicleMarketSources());
      }
      if (include(DATA_SOURCE_POLL_DOMAINS.finance) || fullLoad) {
        requestKeys.push("finance");
        requests.push(listFinanceFxAdminSources());
      }
      if (include(DATA_SOURCE_POLL_DOMAINS.ga4) || fullLoad) {
        requestKeys.push("ga4");
        requests.push(listGa4AnalyticsAdminSources());
      }
      // Keep schedule overlays when polling culture/finance so Manage schedule labels stay accurate.
      if (
        fullLoad ||
        include(DATA_SOURCE_POLL_DOMAINS.culture) ||
        include(DATA_SOURCE_POLL_DOMAINS.finance)
      ) {
        requestKeys.push("overlay");
        requests.push(listDataSourceSchedules());
      }
      if (fullLoad) {
        requestKeys.push("transportJobs");
        requests.push(fetchTransportSchedulerJobs());
      }

      const settled = await Promise.allSettled(requests);
      const byKey = Object.fromEntries(
        requestKeys.map((key, index) => [key, settled[index]])
      );

      const overlayByKey = overlayScheduleMap(
        byKey.overlay?.status === "fulfilled" ? byKey.overlay.value?.schedules || [] : []
      );
      const transportJobs =
        byKey.transportJobs?.status === "fulfilled"
          ? byKey.transportJobs.value?.jobs || []
          : [];

      if (byKey.realEstate) {
        if (byKey.realEstate.status === "fulfilled") {
          setRealEstateSources(byKey.realEstate.value.sources || []);
        } else if (fullLoad) {
          setRealEstateSources([]);
          setRealEstateErrorMessage(
            byKey.realEstate.reason?.message || "Unable to load real estate sources."
          );
        }
      }

      if (byKey.weather) {
        if (byKey.weather.status === "fulfilled") {
          setWeatherSources(byKey.weather.value.sources || []);
        } else if (fullLoad) {
          setWeatherSources([]);
          setWeatherErrorMessage(
            byKey.weather.reason?.message || "Unable to load weather sources."
          );
        }
      }

      if (byKey.automotive) {
        if (byKey.automotive.status === "fulfilled") {
          setAutomotiveSources(byKey.automotive.value.sources || []);
        } else if (fullLoad) {
          setAutomotiveSources([]);
          setAutomotiveErrorMessage(
            byKey.automotive.reason?.message ||
              "Unable to load Vehicle Market production readiness data."
          );
        }
      }

      if (byKey.culture) {
        if (byKey.culture.status === "fulfilled") {
          setCultureSources(
            (byKey.culture.value.sources || []).map((source) =>
              applyScheduleOverlay(source, overlayByKey, "culture")
            )
          );
          if (fullLoad) {
            setCultureErrorMessage("");
          }
        } else if (fullLoad) {
          setCultureSources([]);
          setCultureErrorMessage(
            byKey.culture.reason?.message || "Unable to load culture admin sources."
          );
        }
      }

      if (byKey.adminSources) {
        if (byKey.adminSources.status === "fulfilled") {
          const all = byKey.adminSources.value || [];
          setDomainSources({
            food: all
              .filter((source) => source.sourceDomain === "food")
              .map((source) => applyScheduleOverlay(source, overlayByKey, "food")),
            transport: all
              .filter((source) => source.sourceDomain === "transport")
              .map((source) => {
                const withJob = mergeTransportJobSchedule(source, transportJobs);
                if (withJob.transportJobKey) {
                  return withJob;
                }
                return applyScheduleOverlay(withJob, overlayByKey, "transport");
              }),
            culture: all.filter((source) => source.sourceDomain === "culture"),
            automotive: all.filter((source) => source.sourceDomain === "automotive"),
            vehicle: all.filter((source) => source.sourceDomain === "vehicle"),
            vendor_directory: all.filter((source) => source.sourceDomain === "vendor_directory"),
          });
        } else {
          setDomainSources({
            food: [],
            transport: [],
            culture: [],
            automotive: [],
            vehicle: [],
            vendor_directory: [],
          });
        }
      }

      if (byKey.vehicleMarket) {
        if (byKey.vehicleMarket.status === "fulfilled") {
          setVehicleMarketSources(byKey.vehicleMarket.value.sources || []);
          setVehicleMarketErrorMessage("");
        } else {
          setVehicleMarketSources([]);
          setVehicleMarketErrorMessage(
            byKey.vehicleMarket.reason?.message || "Unable to load Vehicle Market sources."
          );
        }
      }

      if (byKey.finance) {
        if (byKey.finance.status === "fulfilled") {
          setFinanceSources(
            (byKey.finance.value.sources || []).map((source) =>
              applyScheduleOverlay(source, overlayByKey, "finance")
            )
          );
          setFinanceErrorMessage("");
        } else if (fullLoad) {
          setFinanceSources([]);
          setFinanceErrorMessage(
            byKey.finance.reason?.message || "Unable to load Finance FX sources."
          );
        }
      }

      if (byKey.ga4) {
        if (byKey.ga4.status === "fulfilled") {
          setGa4Sources(byKey.ga4.value.sources || []);
          setGa4ErrorMessage("");
        } else if (fullLoad) {
          setGa4Sources([]);
          setGa4ErrorMessage(
            byKey.ga4.reason?.message || "Unable to load Google Analytics sources."
          );
        }
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, []);

  const handleSyncRegistry = useCallback(async () => {
    try {
      setIsSyncing(true);
      const data = await syncRealEstateSources();
      setRealEstateSources(data.sources || []);
      showToast("Registry synced with latest broker sources.", "success");
    } catch (error) {
      showToast(error.message || "Unable to sync registry.", "error");
    } finally {
      setIsSyncing(false);
    }
  }, [showToast]);

  const runningDomains = useMemo(
    () =>
      resolveRunningPollDomains({
        realEstateSources,
        weatherSources,
        automotiveSources,
        cultureSources,
        financeSources,
        ga4Sources,
      }),
    [realEstateSources, weatherSources, automotiveSources, cultureSources, financeSources, ga4Sources]
  );

  runningDomainsRef.current = runningDomains;
  const hasRunningSource = runningDomains.length > 0;

  const errorMessage = useMemo(() => {
    const errors = [realEstateErrorMessage, weatherErrorMessage].filter(Boolean);
    if (errors.length >= 2) {
      return "Unable to load some data sources.";
    }
    return errors[0] || "";
  }, [realEstateErrorMessage, weatherErrorMessage]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (!hasRunningSource) {
      return undefined;
    }

    pollRef.current = setInterval(() => {
      const domains = runningDomainsRef.current;
      if (!domains.length) {
        return;
      }
      loadSources({ silent: true, domains });
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [hasRunningSource, loadSources]);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }

    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  return {
    realEstateSources,
    setRealEstateSources,
    weatherSources,
    setWeatherSources,
    automotiveSources,
    setAutomotiveSources,
    cultureSources,
    setCultureSources,
    domainSources,
    setDomainSources,
    isLoading,
    isSyncing,
    errorMessage,
    realEstateErrorMessage,
    weatherErrorMessage,
    automotiveErrorMessage,
    cultureErrorMessage,
    vehicleMarketSources,
    vehicleMarketErrorMessage,
    financeSources,
    setFinanceSources,
    financeErrorMessage,
    ga4Sources,
    setGa4Sources,
    ga4ErrorMessage,
    toast,
    showToast,
    loadSources,
    handleSyncRegistry,
    hasRunningSource,
  };
}
